/**
 * The single source of truth for element geometry.
 *
 * `drawableFor` turns an element into primitive ops expressed in ELEMENT-LOCAL,
 * UNROTATED, CSS-pixel coordinates. It bakes in no device pixel ratio, no zoom,
 * no scroll and no element transform: each renderer applies those itself.
 *
 * There are only two op kinds and no shape primitives. Ellipses are four cubic
 * Beziers, rounded corners are hand-built arcs. Native `ctx.ellipse` and SVG
 * `<ellipse>` disagree about start angle, so their dash phase differs; building
 * the curve ourselves makes canvas output, SVG output, hit testing and arrow
 * binding trace literally the same path.
 */

import type { Point, SlateElement, StrokeStyle, FontFamily } from '../scene/types';
import { isText } from '../scene/types';

export type PathCmd =
  | { readonly c: 'M'; readonly p: Point }
  | { readonly c: 'L'; readonly p: Point }
  | { readonly c: 'C'; readonly c1: Point; readonly c2: Point; readonly p: Point }
  | { readonly c: 'Z' };

export interface Stroke {
  readonly color: string;
  readonly width: number;
  readonly dash: readonly number[] | null;
  readonly cap: 'butt' | 'round';
  readonly join: 'round' | 'miter';
}

export interface Fill {
  readonly color: string;
}

export interface FontSpec {
  readonly family: string;
  readonly size: number;
  readonly weight: number;
  readonly style: 'normal' | 'italic';
  /** Absolute px, computed once so canvas and SVG cannot drift. */
  readonly lineHeight: number;
}

export interface TextLine {
  readonly text: string;
  /** Absolute local x of the line start. All text is drawn left-anchored. */
  readonly x: number;
  /** Absolute local y of the alphabetic baseline. */
  readonly y: number;
}

export type Op =
  | {
      readonly k: 'path';
      readonly cmds: readonly PathCmd[];
      readonly fill: Fill | null;
      readonly stroke: Stroke | null;
      /** Interiors are hit-testable only when the path is closed. */
      readonly closed: boolean;
    }
  | {
      readonly k: 'text';
      readonly lines: readonly TextLine[];
      readonly font: FontSpec;
      readonly color: string;
    };

export interface Drawable {
  readonly ops: readonly Op[];
  /** 0..1, applied by the renderer to the element as a whole. */
  readonly opacity: number;
}

export interface TextMetrics {
  readonly width: number;
  readonly ascent: number;
  readonly descent: number;
}

/**
 * Injected text measurement. The canvas implementation uses a cached offscreen
 * 2D context; tests inject a deterministic monospace stub. jsdom has no 2D
 * context, so this injection is what makes text testable at all.
 */
export type MeasureText = (text: string, font: FontSpec) => TextMetrics;

/** Circle-to-cubic-Bezier control point ratio. */
export const KAPPA = 0.5522847498307936;

export const CONTAINER_PADDING = 8;

/**
 * Arrowhead sizing, in scene units, so heads scale with zoom like everything
 * else. The length is the larger of a floor and a multiple of the stroke width,
 * so thick arrows keep proportionate heads. Raise ARROWHEAD_MIN_LENGTH to make
 * every arrowhead bigger.
 */
export const ARROWHEAD_MIN_LENGTH = 16;
export const ARROWHEAD_STROKE_RATIO = 7;
/** Half the barb spread, as a fraction of the head length. */
export const ARROWHEAD_SPREAD = 0.52;

export const FONT_STACKS: Record<FontFamily, string> = {
  sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
};

export function lineHeightFor(fontSize: number): number {
  return Math.round(fontSize * 1.25);
}

export function fontSpecFor(family: FontFamily, size: number): FontSpec {
  return {
    family: FONT_STACKS[family],
    size,
    weight: 400,
    style: 'normal',
    lineHeight: lineHeightFor(size),
  };
}

/**
 * The canonical CSS `font` shorthand. The canvas painter, the canvas measurer
 * and the editing textarea must all use this exact string, or the caret and the
 * rendered wrap visibly disagree.
 */
export function fontShorthand(font: FontSpec): string {
  return `${font.style} ${font.weight} ${font.size}px/${font.lineHeight}px ${font.family}`;
}

/** Absolute px dash patterns derived from stroke width. */
export function dashArrayFor(style: StrokeStyle, strokeWidth: number): readonly number[] | null {
  switch (style) {
    case 'solid':
      return null;
    case 'dashed':
      return [8, 8 + strokeWidth * 2];
    case 'dotted':
      return [strokeWidth / 1.5, strokeWidth * 3];
  }
}

export function roundedRectCmds(w: number, h: number, radius: number): PathCmd[] {
  const r = Math.max(0, Math.min(radius, w / 2, h / 2));
  if (r === 0) {
    return [
      { c: 'M', p: [0, 0] },
      { c: 'L', p: [w, 0] },
      { c: 'L', p: [w, h] },
      { c: 'L', p: [0, h] },
      { c: 'Z' },
    ];
  }
  const k = r * KAPPA;
  return [
    { c: 'M', p: [r, 0] },
    { c: 'L', p: [w - r, 0] },
    { c: 'C', c1: [w - r + k, 0], c2: [w, r - k], p: [w, r] },
    { c: 'L', p: [w, h - r] },
    { c: 'C', c1: [w, h - r + k], c2: [w - r + k, h], p: [w - r, h] },
    { c: 'L', p: [r, h] },
    { c: 'C', c1: [r - k, h], c2: [0, h - r + k], p: [0, h - r] },
    { c: 'L', p: [0, r] },
    { c: 'C', c1: [0, r - k], c2: [r - k, 0], p: [r, 0] },
    { c: 'Z' },
  ];
}

export function ellipseCmds(w: number, h: number): PathCmd[] {
  const rx = w / 2;
  const ry = h / 2;
  const kx = rx * KAPPA;
  const ky = ry * KAPPA;
  return [
    { c: 'M', p: [0, ry] },
    { c: 'C', c1: [0, ry - ky], c2: [rx - kx, 0], p: [rx, 0] },
    { c: 'C', c1: [rx + kx, 0], c2: [w, ry - ky], p: [w, ry] },
    { c: 'C', c1: [w, ry + ky], c2: [rx + kx, h], p: [rx, h] },
    { c: 'C', c1: [rx - kx, h], c2: [0, ry + ky], p: [0, ry] },
    { c: 'Z' },
  ];
}

export function diamondCmds(w: number, h: number): PathCmd[] {
  return [
    { c: 'M', p: [w / 2, 0] },
    { c: 'L', p: [w, h / 2] },
    { c: 'L', p: [w / 2, h] },
    { c: 'L', p: [0, h / 2] },
    { c: 'Z' },
  ];
}

export function polylineCmds(points: readonly Point[]): PathCmd[] {
  if (points.length === 0) return [];
  const cmds: PathCmd[] = [{ c: 'M', p: points[0]! }];
  for (let i = 1; i < points.length; i++) cmds.push({ c: 'L', p: points[i]! });
  return cmds;
}

/**
 * A filled triangle at `tip`, pointing away from `from`. SVG <marker> is never
 * used: markers scale with stroke width and orient by their own rules, and have
 * no canvas analogue, so they are guaranteed to diverge between the two.
 */
export function arrowheadCmds(tip: Point, from: Point, size: number): PathCmd[] {
  const dx = tip[0] - from[0];
  const dy = tip[1] - from[1];
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return [];
  const ux = dx / len;
  const uy = dy / len;
  const s = Math.min(size, len);
  const baseX = tip[0] - ux * s;
  const baseY = tip[1] - uy * s;
  const halfWidth = s * ARROWHEAD_SPREAD;
  return [
    { c: 'M', p: [baseX - uy * halfWidth, baseY + ux * halfWidth] },
    { c: 'L', p: [tip[0], tip[1]] },
    { c: 'L', p: [baseX + uy * halfWidth, baseY - ux * halfWidth] },
    { c: 'Z' },
  ];
}

function cornerRadiusFor(w: number, h: number): number {
  return Math.min(12, w * 0.2, h * 0.2);
}

/**
 * Greedy word wrap. Only bound text wraps; standalone text splits on newlines
 * alone, so what the user typed is what they get.
 */
export function wrapText(
  text: string,
  maxWidth: number,
  font: FontSpec,
  measure: MeasureText,
): string[] {
  const out: string[] = [];
  for (const paragraph of text.split('\n')) {
    if (!Number.isFinite(maxWidth) || maxWidth <= 0) {
      out.push(paragraph);
      continue;
    }
    const words = paragraph.split(' ');
    let line = '';
    for (const word of words) {
      const candidate = line === '' ? word : `${line} ${word}`;
      // Measure with trailing space trimmed for the break decision, otherwise
      // centred text drifts by the width of a space.
      if (measure(candidate, font).width <= maxWidth || line === '') {
        line = candidate;
      } else {
        out.push(line);
        line = word;
      }
    }
    out.push(line);
  }
  return out.length === 0 ? [''] : out;
}

export function textLinesFor(el: SlateElement, measure: MeasureText): string[] {
  if (!isText(el)) return [];
  const font = fontSpecFor(el.fontFamily, el.fontSize);
  const maxWidth = el.containerId ? el.width : Infinity;
  return wrapText(el.text, maxWidth, font, measure);
}

/** Natural size of a text element's content, used to auto-grow while typing. */
export function measureTextBlock(
  lines: readonly string[],
  font: FontSpec,
  measure: MeasureText,
): { width: number; height: number } {
  let width = 0;
  for (const line of lines) width = Math.max(width, measure(line, font).width);
  return { width, height: Math.max(font.lineHeight, lines.length * font.lineHeight) };
}

function textOp(el: SlateElement, measure: MeasureText): Op | null {
  if (!isText(el)) return null;
  const font = fontSpecFor(el.fontFamily, el.fontSize);
  const lines = textLinesFor(el, measure);
  const ascent = measure('Mg', font).ascent;
  const out: TextLine[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const w = measure(line, font).width;
    let x = 0;
    if (el.textAlign === 'center') x = (el.width - w) / 2;
    else if (el.textAlign === 'right') x = el.width - w;
    out.push({ text: line, x, y: ascent + i * font.lineHeight });
  }
  return { k: 'text', lines: out, font, color: el.strokeColor };
}

export function drawableFor(el: SlateElement, measure: MeasureText): Drawable {
  const stroke: Stroke = {
    color: el.strokeColor,
    width: el.strokeWidth,
    dash: dashArrayFor(el.strokeStyle, el.strokeWidth),
    cap: el.type === 'freedraw' ? 'round' : 'butt',
    join: 'round',
  };
  const fill: Fill | null = el.fillStyle === 'solid' ? { color: el.backgroundColor } : null;
  const ops: Op[] = [];

  switch (el.type) {
    case 'rect':
      ops.push({
        k: 'path',
        cmds: roundedRectCmds(el.width, el.height, cornerRadiusFor(el.width, el.height)),
        fill,
        stroke,
        closed: true,
      });
      break;
    case 'ellipse':
      ops.push({ k: 'path', cmds: ellipseCmds(el.width, el.height), fill, stroke, closed: true });
      break;
    case 'diamond':
      ops.push({ k: 'path', cmds: diamondCmds(el.width, el.height), fill, stroke, closed: true });
      break;
    case 'freedraw':
      ops.push({
        k: 'path',
        cmds: polylineCmds(el.points),
        fill: null,
        stroke: { ...stroke, cap: 'round' },
        closed: false,
      });
      break;
    case 'arrow': {
      const pts = el.points;
      ops.push({ k: 'path', cmds: polylineCmds(pts), fill: null, stroke, closed: false });
      if (pts.length >= 2) {
        // Total length decides whether heads are drawn at all: an inverted head
        // on a near-zero-length arrow looks broken.
        let total = 0;
        for (let i = 1; i < pts.length; i++) {
          total += Math.hypot(pts[i]![0] - pts[i - 1]![0], pts[i]![1] - pts[i - 1]![1]);
        }
        const size = Math.max(ARROWHEAD_MIN_LENGTH, el.strokeWidth * ARROWHEAD_STROKE_RATIO);
        if (total >= 4) {
          const headFill: Fill = { color: el.strokeColor };
          if (el.endArrowhead) {
            const cmds = arrowheadCmds(pts[pts.length - 1]!, pts[pts.length - 2]!, size);
            if (cmds.length) ops.push({ k: 'path', cmds, fill: headFill, stroke: null, closed: true });
          }
          if (el.startArrowhead) {
            const cmds = arrowheadCmds(pts[0]!, pts[1]!, size);
            if (cmds.length) ops.push({ k: 'path', cmds, fill: headFill, stroke: null, closed: true });
          }
        }
      }
      break;
    }
    case 'text': {
      const op = textOp(el, measure);
      if (op) ops.push(op);
      break;
    }
  }

  return { ops, opacity: el.opacity };
}
